/**
 * FormPokemonCard - Card component for Alternate Forms (Deoxys, Rotom, etc.) in the main grid
 */
import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Heart, Boxes } from 'lucide-react';
import { TypeBadgeGroup } from './TypeBadge';
import { useApp } from '../../context/AppContext';
import { Skeleton } from '../ui/skeleton';
import { cn } from '../../lib/utils';

export function FormPokemonCard({ pokemon, index = 0 }) {
  const { isFavorite, toggleFavorite } = useApp();
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const pokemonId = pokemon.id;
  const favorite = isFavorite(pokemonId);
  
  const handleFavoriteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite(pokemonId);
  };
  
  // Format display name
  const displayName = pokemon.displayName || pokemon.name.replace(/-/g, ' ');
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.02 }}
      whileHover={{ y: -4 }}
      className="group"
    >
      <Link 
        to={`/pokemon/${pokemon.apiId || pokemon.id}`}
        data-testid={`pokemon-card-form-${pokemonId}`}
        className="block"
      >
        <div className={cn(
          "relative bg-card rounded-2xl border-2 border-indigo-500/50 hover:border-indigo-500 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden"
        )}>
          {/* Form indicator badge */}
          <div className="absolute top-0 left-0 right-0 py-1 px-3 flex items-center gap-1.5 z-10 bg-indigo-500">
            <Boxes className="w-3 h-3 text-white" />
            <span className="text-[10px] font-bold text-white uppercase tracking-wider">FORM</span>
          </div>
          
          {/* ID watermark */}
          <span className="absolute top-10 left-3 text-4xl font-black text-muted-foreground/10 select-none pointer-events-none">
            #{String(pokemon.baseId).padStart(3, '0')}
          </span>
          
          {/* Favorite button */}
          <button
            onClick={handleFavoriteClick}
            data-testid={`favorite-btn-form-${pokemonId}`}
            className="absolute top-8 right-3 z-10 p-2 rounded-full bg-white/80 dark:bg-black/50 backdrop-blur-sm hover:scale-110 transition-transform"
          >
            <Heart 
              className={`w-5 h-5 transition-colors ${
                favorite 
                  ? 'fill-rose-500 text-rose-500' 
                  : 'text-muted-foreground hover:text-rose-500'
              }`}
            />
          </button>
          
          {/* Pokemon image */}
          <div className="relative pt-10 pb-4 px-4 flex items-center justify-center h-44">
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Skeleton className="w-32 h-32 rounded-full" />
              </div>
            )}
            <div className="relative">
              <img
                src={pokemon.spriteUrl}
                alt={displayName}
                loading="lazy"
                onLoad={() => setImageLoaded(true)}
                onError={(e) => {
                  e.target.src = `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${pokemon.baseId}.png`;
                  setImageLoaded(true);
                }}
                className={`w-32 h-32 object-contain drop-shadow-lg group-hover:scale-110 transition-transform duration-300 ${
                  imageLoaded ? 'opacity-100' : 'opacity-0'
                }`}
              />
            </div>
          </div>
          
          {/* Pokemon info */}
          <div className="p-4 pt-0 space-y-2">
            <p className="text-xs font-bold uppercase tracking-widest flex items-center gap-1 text-indigo-600 dark:text-indigo-400">
              <Boxes className="w-3 h-3" />
              #{String(pokemon.baseId).padStart(3, '0')}
            </p>
            <h3 className="text-lg font-bold capitalize text-foreground truncate">
              {displayName}
            </h3>
            <TypeBadgeGroup types={pokemon.types} size="xs" />
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export default FormPokemonCard;
